// Filtering and sorting helpers extracted from App.js
// Keep legacy-friendly syntax (no optional chaining, etc.)

/**
 * Filter apps based on search, categories, source, and vendor
 */
export function filterApps(apps, options) {
    var searchQuery = (options.searchQuery || '').toLowerCase();
    var selectedCategories = options.selectedCategories || [];
    var selectedSource = options.selectedSource || 'all';
    var selectedVendor = options.selectedVendor || '';

    return apps.filter(function(app) {
        if (searchQuery) {
            var nameMatch = app.app_name.toLowerCase().indexOf(searchQuery) !== -1;
            var vendorMatch = app.vendor.toLowerCase().indexOf(searchQuery) !== -1;
            var descMatch = app.description.toLowerCase().indexOf(searchQuery) !== -1;
            var pkgMatch = app.package_name.toLowerCase().indexOf(searchQuery) !== -1;
            if (!nameMatch && !vendorMatch && !descMatch && !pkgMatch) {
                return false;
            }
        }
        if (selectedCategories.length > 0) {
            if (selectedCategories.indexOf(app.category) === -1) {
                return false;
            }
        }
        if (selectedSource !== 'all' && app.source !== selectedSource) {
            return false;
        }
        if (selectedVendor && app.vendor !== selectedVendor) {
            return false;
        }
        return true;
    });
}

function getNetScore(app) {
    var up = 0;
    var down = 0;
    if (app && app.voteData) {
        up = app.voteData.upvotes || 0;
        down = app.voteData.downvotes || 0;
    } else if (app) {
        up = app.upvotes || 0;
        down = app.downvotes || 0;
    }
    return (up || 0) - (down || 0);
}

function ratingTierAndValue(app, direction) {
    var net = getNetScore(app);
    var ratingVal = (app && app.rating) ? app.rating : 0;
    var hasRating = ratingVal > 0;

    // Grouping tiers:
    // ▼ (desc): upvoted → rated → downvoted → no rating
    // ▲ (asc): downvoted → upvoted → rated → no rating
    var tier = 3;
    if (direction === 'asc') {
        if (net < 0) tier = 0;
        else if (net > 0) tier = 1;
        else if (net === 0 && hasRating) tier = 2;
    } else {
        if (net > 0) tier = 0;
        else if (net === 0 && hasRating) tier = 1;
        else if (net < 0) tier = 2;
    }

    // Within tiers, sort by net + rating per direction.
    // (Matches server ordering; direction applies inside each tier.)
    return { tier: tier, net: net, rating: ratingVal };
}

/**
 * Apply ordered sorts; if no sorts, the caller should shuffle.
 */
export function sortApps(apps, activeSorts, voteLookup) {
    var arr = apps.slice();
    if (!activeSorts || activeSorts.length === 0) {
        return arr;
    }
    arr.sort(function(a, b) {
        var compareVal = function(aValue, bValue, direction) {
            var dir = direction === 'asc' ? 'asc' : 'desc';
            if (aValue < bValue) return dir === 'asc' ? -1 : 1;
            if (aValue > bValue) return dir === 'asc' ? 1 : -1;
            return 0;
        };
        for (var s = 0; s < activeSorts.length; s++) {
            var sort = activeSorts[s];
            var aVal, bVal;
            switch (sort.field) {
                case 'name':
                    aVal = a.app_name.toLowerCase();
                    bVal = b.app_name.toLowerCase();
                    break;
                case 'rating':
                    var aRank = ratingTierAndValue(a, sort.direction);
                    var bRank = ratingTierAndValue(b, sort.direction);
                    var tierCmp = compareVal(aRank.tier, bRank.tier, 'asc');
                    if (tierCmp !== 0) return tierCmp;
                    var netCmp = compareVal(aRank.net, bRank.net, sort.direction);
                    if (netCmp !== 0) return netCmp;
                    var ratingCmp = compareVal(aRank.rating, bRank.rating, sort.direction);
                    if (ratingCmp !== 0) return ratingCmp;
                    // Equal for rating; move to next sort field.
                    continue;
                case 'size':
                    var aSize = a.file_size_mb || 0;
                    var bSize = b.file_size_mb || 0;
                    var aSizeMissing = !aSize || aSize <= 0;
                    var bSizeMissing = !bSize || bSize <= 0;
                    if (aSizeMissing !== bSizeMissing) {
                        return aSizeMissing ? 1 : -1; // missing always last
                    }
                    aVal = aSize;
                    bVal = bSize;
                    break;
                case 'updated':
                    var aDate = a.last_updated || '';
                    var bDate = b.last_updated || '';
                    var aMissing = !aDate || String(aDate).trim() === '';
                    var bMissing = !bDate || String(bDate).trim() === '';
                    if (aMissing !== bMissing) {
                        return aMissing ? 1 : -1; // missing always last
                    }
                    aVal = aDate;
                    bVal = bDate;
                    break;
                default:
                    aVal = a.app_name.toLowerCase();
                    bVal = b.app_name.toLowerCase();
            }
            if (aVal < bVal) return sort.direction === 'asc' ? -1 : 1;
            if (aVal > bVal) return sort.direction === 'asc' ? 1 : -1;
        }
        return 0;
    });
    return arr;
}

/**
 * Fisher–Yates shuffle
 */
export function shuffleApps(apps) {
    var arr = apps.slice();
    for (var i = arr.length - 1; i > 0; i--) {
        var j = Math.floor(Math.random() * (i + 1));
        var tmp = arr[i];
        arr[i] = arr[j];
        arr[j] = tmp;
    }
    return arr;
}

function mulberry32(seed) {
    var t = seed + 0x6D2B79F5;
    t = Math.imul(t ^ t >>> 15, t | 1);
    t ^= t + Math.imul(t ^ t >>> 7, t | 61);
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
}

function seededShuffle(apps, seed) {
    if (seed === undefined || seed === null) return shuffleApps(apps);
    var arr = apps.slice();
    var s = seed >>> 0;
    for (var i = arr.length - 1; i > 0; i--) {
        s = (s + 0x6D2B79F5) >>> 0;
        var r = mulberry32(s);
        var j = Math.floor(r * (i + 1));
        var tmp = arr[i];
        arr[i] = arr[j];
        arr[j] = tmp;
    }
    return arr;
}

/**
 * Combined filter and sort helper
 */
export function filterAndSort(apps, options) {
    var filtered = options && options.serverFiltered ? apps : filterApps(apps, options);
    if (!options.activeSorts || options.activeSorts.length === 0) {
        if (options && options.serverOrdered) {
            return filtered;
        }
        if (options && options.randomSeed !== undefined && options.randomSeed !== null) {
            return seededShuffle(filtered, options.randomSeed);
        }
        return shuffleApps(filtered);
    }
    if (options && options.serverOrdered) {
        return filtered;
    }
    return sortApps(filtered, options.activeSorts, options.voteLookup);
}
